"use client";

import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "@/lib/auth";
import { useGroupEditor } from "@/hooks/useGroupEditor";
import { InterestFormModal } from "@/components/forms/InterestFormModal";
import GigModal from "@/components/gigs/GigModal";
import { Clock, Award } from "lucide-react";

export type JobDescription = {
    shape_the_future?: string;
    join_the_cmt_association?: string;
    mission?: string;
    how_to_get_involved?: string;
    updated_at?: string;
    updated_by?: { id: string; name: string };
};

type AcfData = Record<string, unknown>;

type Props = {
    entityType: string;
    entityId: string | number;
    groupName: string;
    pipelineKey?: string;
    initialJd: JobDescription;
    initialAcf?: AcfData;
};

const API_BASE = (typeof window !== "undefined")
    ? ""
    : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001");

const PLURAL: Record<string, string> = {
    chapter: "chapters",
    community: "communities",
    committee: "committees",
};

// Stop-words used to find the natural title boundary in a verb-phrase bullet.
const STOP_WORDS = /^(of|and|between|to|from|by|with|for|in|on|or|that|the|which|when|where|while|as)\b/i;

/**
 * Extracts a {title, body} pair from a plain bullet string.
 *
 * Priority:
 *  1. "Title: body" format  — split on first ": " if title ≤ 7 words
 *  2. Verb-phrase split      — stop at the first stop-word that appears after
 *     the 3rd word (so title has meaningful substance)
 *  3. Short bullet (≤ 55 chars) — the whole text becomes the title, body = ""
 *  4. First-4-words fallback
 */
function parseTitled(text: string): { title: string; body: string } {
    const clean = text.replace(/[.!?]$/, "").trim();

    // 1. Split on first occurrence of ": ", " — ", " – ", or " - " (dash/em-dash/en-dash with spaces)
    const match = clean.match(/(?::\s+|\s+—\s+|\s+–\s+|\s+-\s+)/);
    if (match && match.index !== undefined && match.index > 0 && match.index <= 60) {
        const candidate = clean.slice(0, match.index);
        if (candidate.split(/\s+/).length <= 7) {
            return { title: candidate, body: clean.slice(match.index + match[0].length) };
        }
    }

    // 2. Short phrase — use as title only (check BEFORE stop-word split)
    if (clean.length <= 60) {
        return { title: clean, body: "" };
    }

    // 3. Stop-word split for longer verb-phrase sentences
    const words = clean.split(/\s+/);
    for (let i = 3; i < words.length; i++) {
        if (STOP_WORDS.test(words[i])) {
            return { title: words.slice(0, i).join(" "), body: clean };
        }
    }

    // 4. First-4-words fallback
    return { title: words.slice(0, 4).join(" "), body: clean };
}

const TEAL = "#007a8a";
const DARK = "#003d45";
const BODY = "#1a1a1a";
const MUTED = "#666666";
const BRAND_RED = "#ae232d";

function PencilIcon() {
    return (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L7.5 21H4v-3.5L16.732 3.732z" />
        </svg>
    );
}

function EditTrigger({ onClick, label = "Edit" }: { onClick: () => void; label?: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-track="group-edit-open"
            data-track-form-open="group-edit"
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: TEAL }}
            aria-label={label}
        >
            <PencilIcon />
            {label}
        </button>
    );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
    return (
        <h3 className="text-xl font-bold mb-4 pl-3 border-l-4" style={{ color: DARK, borderColor: TEAL }}>{children}</h3>
    );
}

const JD_TABS = [
    { key: "what_we_do",              label: "What We Do" },
    { key: "why_serve",               label: "Why Serve" },
    { key: "what_to_expect",          label: "What to Expect" },
    { key: "is_this_right_for_you",   label: "Right for You?" },
    { key: "how_to_get_involved",     label: "Get Involved" },
] as const;

type JdTabKey = typeof JD_TABS[number]["key"];

function parseBullets(raw: unknown): string[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map(l => String(l).replace(/^[•\-]\s*/, "").trim()).filter(Boolean);
    }
    if (typeof raw !== "string") return [];
    return raw.split("\n").map(l => l.replace(/^[•\-]\s*/, "").trim()).filter(Boolean);
}

function parseSemicolons(raw: unknown): string[] {
    if (!raw || typeof raw !== "string") return [];
    return raw.split(";").map(s => s.trim()).filter(Boolean);
}

function acfStr(acf: AcfData, key: string): string {
    const v = acf[key];
    return v && typeof v === "string" ? v : "";
}

export default function JobDescriptionPanel({ entityType, entityId, groupName, pipelineKey, initialJd, initialAcf = {} }: Props) {
    const canEdit = useGroupEditor(entityType, entityId);
    const [jd, setJd] = useState<JobDescription>(initialJd);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<string>("");
    const [editing, setEditing] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<JdTabKey>("how_to_get_involved");

    // Gigs integration states
    const [gigs, setGigs] = useState<any[]>([]);
    const [isLoadingGigs, setIsLoadingGigs] = useState(true);
    const [selectedGigId, setSelectedGigId] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        async function fetchGigs() {
            setIsLoadingGigs(true);
            try {
                const res = await fetchWithAuth(`/api/v1/gigs/catalog?group=${entityType}:${entityId}`);
                if (res.ok && active) {
                    const data = await res.json();
                    setGigs(data || []);
                }
            } catch (err) {
                console.error("Failed to fetch gigs for group:", err);
            } finally {
                if (active) setIsLoadingGigs(false);
            }
        }
        fetchGigs();
        return () => {
            active = false;
        };
    }, [entityType, entityId]);

    // Discover → "Apply now" lands on #get-involved; the page renders after
    // its data loads, so the browser's own hash jump has already missed.
    useEffect(() => {
        if (window.location.hash === "#get-involved") {
            document.getElementById("get-involved")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    const plural = PLURAL[entityType] || `${entityType}s`;
    const endpoint = `${API_BASE}/api/v1/organizations/${plural}/${entityId}/job-description`;

    const displayGroupName = useMemo(() => groupName || `the ${entityType}`, [groupName, entityType]);

    async function save(partial: Partial<JobDescription>) {
        setSaving(true);
        try {
            const res = await fetchWithAuth(endpoint, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(partial),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `Save failed (${res.status})`);
            }
            const data = await res.json();
            setJd(data.job_description as JobDescription);
            setEditing(null);
            setToast("Saved");
        } catch (e) {
            setToast(e instanceof Error ? e.message : "Save failed");
        } finally {
            setSaving(false);
            setTimeout(() => setToast(""), 2500);
        }
    }

    const wrap = (sectionKey: string, children: React.ReactNode, onEdit?: () => void) => (
        <div className="group relative">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">{children}</div>
                {canEdit && onEdit && <EditTrigger onClick={onEdit} />}
            </div>
        </div>
    );

    // Default "Why Serve" benefits per entity type (shown when acf_data.why_serve is empty)
    const DEFAULT_WHY_SERVE: Record<string, string[]> = {
        chapter: [
            "Build local leadership — Develop organizational and leadership skills by running chapter programming and events",
            "Grow your network — Connect with fellow charterholders, candidates, and market practitioners in your region",
            "Represent the profession — Serve as a local ambassador for technical analysis and the CMT designation",
            "Shape regional impact — Drive awareness of the CMT Program at universities and employers in your market",
        ],
        community: [
            "Connect with like-minded practitioners — Engage with members who share your specific interest in a technical technique, market niche, or topic",
            "Curate specialized content — Lead discussions and share insights that deepen expertise across the community",
            "Build a niche network — Form relationships with members who have deep knowledge in your area of focus",
            "Advance the field — Contribute to the broader body of knowledge in technical analysis through your community's work",
        ],
    };

    // Default core responsibilities for communities
    const DEFAULT_COMMUNITY_RESPONSIBILITIES = [
        "Member engagement — Foster a vibrant local culture of connection, learning, and camaraderie among all members and candidates in the community's geography.",
        "Candidate support — Champion the success of CMT Program candidates through peer encouragement, study support, and local resources.",
        "University outreach — Build and maintain relationships with local universities and business schools to grow awareness of technical analysis and the CMT designation.",
        "Employer advocacy — Connect with local employers to advance recognition of the CMT designation and create professional development pathways for charterholders.",
        "Thematic inclusion — Advance the local presence of CMT Association's thematic initiatives, including Women in Technical Analysis, Global Black Caucus, and Dual Charter Initiative, through programming and awareness within the community.",
        "Volunteer activation — Cultivate a pipeline of engaged local volunteers who contribute to the community's mission and grow into broader Association leadership.",
    ];

    // Default core activities for communities
    const DEFAULT_COMMUNITY_ACTIVITIES = [
        "Community events — Host in-person meetups at least once per quarter to bring members, candidates, and prospective members together.",
        "University information sessions — Conduct presentations at local universities and business schools to introduce students to technical analysis and the CMT Program.",
        "Academic Partner stewardship — Serve as the local point of contact for institutions in CMT Association's Academic Partner Program, maintaining ongoing engagement and supporting program objectives.",
        "Employer connection program — Organize outreach to local employers highlighting the value of the CMT designation and technical analysis expertise.",
        "Thematic programming — Coordinate local events and initiatives that support Women in Technical Analysis, Global Black Caucus, and Dual Charter Initiative objectives within the community.",
        "Candidate and new member welcome — Provide orientation and peer support for CMT Program candidates and newly admitted Affiliate and Professional Members in the community.",
    ];

    // Tab data from acf_data (WP-synced fields)
    const rawResponsibilities = parseBullets(initialAcf.core_responsibilities);
    const coreResponsibilities = (
        rawResponsibilities.length > 0 ? rawResponsibilities
        : entityType === "community" ? DEFAULT_COMMUNITY_RESPONSIBILITIES
        : []
    ).map(parseTitled);

    const rawActivities = parseBullets(initialAcf.core_activities);
    const coreActivities = (
        rawActivities.length > 0 ? rawActivities
        : entityType === "community" ? DEFAULT_COMMUNITY_ACTIVITIES
        : []
    ).map(parseTitled);

    const rawWhyServe = parseBullets(initialAcf.why_serve);
    const whyServeSource = rawWhyServe.length > 0
        ? rawWhyServe
        : (DEFAULT_WHY_SERVE[entityType] || []);
    const whyServe = whyServeSource.map(parseTitled);

    const meetingFrequency     = acfStr(initialAcf, "meeting_frequency");
    const timeCommitment       = acfStr(initialAcf, "recommended_time_commitment");
    const eligibility          = acfStr(initialAcf, "eligible_cmt_journey_stages");
    const careerDepth          = acfStr(initialAcf, "suggested_career_age_ta_depth");
    const keyCompetencies      = parseSemicolons(initialAcf.key_competencies);

    return (
        <section id="get-involved" style={{ backgroundColor: "#f0f0f0" }} className="py-20 border-b-4 scroll-mt-16" data-border-color={BRAND_RED}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12 max-w-2xl mx-auto">
                    <span className="inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 border" style={{ backgroundColor: "rgba(0,122,138,0.1)", color: DARK, borderColor: "rgba(0,122,138,0.2)" }}>
                        Get Involved
                    </span>
                    <h2 className="text-3xl font-extrabold mb-3" style={{ color: DARK }}>Call to Service</h2>
                    <p className="text-[0.9375rem] leading-relaxed mb-6" style={{ color: BODY }}>
                        Discover how you can contribute your expertise to {displayGroupName}.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8 items-start mb-10">
                    {/* ──────────── JD (left / main) ──────────── */}
                    <article className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                        {/* Hero tagline */}
                        <header className="px-8 py-10 text-white" style={{ backgroundColor: DARK }}>
                            {wrap(
                                "shape_the_future",
                                <>
                                    <div className="text-xs font-bold uppercase tracking-[0.25em] mb-2" style={{ color: "rgba(255,255,255,0.65)" }}>
                                        The Group Experience
                                    </div>
                                    <div className="text-2xl md:text-3xl font-extrabold leading-tight">
                                        {jd.shape_the_future || `Serve ${displayGroupName}`}
                                    </div>
                                </>,
                                canEdit ? () => setEditing("shape_the_future") : undefined
                            )}
                            {jd.join_the_cmt_association && (
                                <div className="mt-5">
                                    {wrap(
                                        "join_the_cmt_association",
                                        <p className="text-[0.9375rem] leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                                            {jd.join_the_cmt_association}
                                        </p>,
                                        canEdit ? () => setEditing("join_the_cmt_association") : undefined
                                    )}
                                </div>
                            )}
                            {!jd.join_the_cmt_association && canEdit && (
                                <div className="mt-5">
                                    <EditTrigger onClick={() => setEditing("join_the_cmt_association")} label="Add intro" />
                                </div>
                            )}
                        </header>

                        {/* Tab bar */}
                        <div className="flex overflow-hidden border-b border-gray-200" style={{ backgroundColor: "#fafafa" }}>
                            {JD_TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setActiveTab(tab.key)}
                                    className="flex-shrink-0 px-5 py-3.5 text-xs font-bold uppercase tracking-widest transition-colors relative"
                                    style={{
                                        color: activeTab === tab.key ? TEAL : MUTED,
                                        borderBottom: activeTab === tab.key ? `2px solid ${TEAL}` : "2px solid transparent",
                                        marginBottom: "-1px",
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                            {canEdit && (
                                <div className="ml-auto flex items-center px-4">
                                    <EditTrigger onClick={() => setEditing(activeTab)} label="Edit section" />
                                </div>
                            )}
                        </div>

                        {/* Tab content */}
                        <div className="px-8 py-8">
                            {activeTab === "what_we_do" && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                                    <div>
                                        {coreResponsibilities.length > 0 ? (
                                            <div>
                                                <h4 className="text-sm font-extrabold uppercase tracking-widest mb-4" style={{ color: TEAL }}>Core Responsibilities</h4>
                                                <ul className="space-y-4">
                                                    {coreResponsibilities.map(({ title, body }, i) => (
                                                        <li key={i} className="pl-4 border-l-2" style={{ borderColor: TEAL }}>
                                                            <div className="font-bold text-[0.95rem]" style={{ color: DARK }}>{title}</div>
                                                            {body && <div className="text-[0.9375rem] mt-1" style={{ color: BODY }}>{body}</div>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <p className="italic text-sm" style={{ color: MUTED }}>No responsibilities defined yet.</p>
                                        )}
                                    </div>
                                    <div>
                                        {coreActivities.length > 0 ? (
                                            <div>
                                                <h4 className="text-sm font-extrabold uppercase tracking-widest mb-4" style={{ color: TEAL }}>Core Activities</h4>
                                                <ul className="space-y-4">
                                                    {coreActivities.map(({ title, body }, i) => (
                                                        <li key={i} className="pl-4 border-l-2" style={{ borderColor: TEAL }}>
                                                            <div className="font-bold text-[0.95rem]" style={{ color: DARK }}>{title}</div>
                                                            {body && <div className="text-[0.9375rem] mt-1" style={{ color: BODY }}>{body}</div>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <p className="italic text-sm" style={{ color: MUTED }}>No activities defined yet.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === "why_serve" && (
                                whyServe.length > 0 ? (
                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        {whyServe.map(({ title, body }, i) => (
                                            <li key={i} className="bg-[#f7faf9] rounded-xl p-5 border border-gray-200">
                                                <div className="font-bold text-[0.95rem]" style={{ color: DARK }}>{title}</div>
                                                {body && <div className="text-sm mt-1.5" style={{ color: BODY }}>{body}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="italic text-sm" style={{ color: MUTED }}>No benefits listed yet.</p>
                                )
                            )}

                            {activeTab === "what_to_expect" && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        { label: "Meeting Frequency",      value: meetingFrequency },
                                        { label: "Time Commitment",        value: timeCommitment },
                                        { label: "Eligibility",            value: eligibility },
                                        { label: "Career / TA Depth",      value: careerDepth },
                                    ].filter(({ value }) => value).map(({ label, value }) => (
                                        <div key={label} className="bg-[#f7faf9] rounded-xl p-4 border border-gray-100">
                                            <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: TEAL }}>{label}</div>
                                            <div className="text-[0.9375rem] font-medium" style={{ color: BODY }}>{value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {activeTab === "is_this_right_for_you" && (
                                keyCompetencies.length > 0 ? (
                                    <ul className="space-y-3">
                                        {keyCompetencies.map((item, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                                                    style={{ backgroundColor: "rgba(0,122,138,0.12)" }}>
                                                    <svg className="w-3 h-3" style={{ color: TEAL }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <span className="text-[0.9375rem]" style={{ color: BODY }}>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="italic text-sm" style={{ color: MUTED }}>No competencies defined yet.</p>
                                )
                            )}

                            {activeTab === "how_to_get_involved" && (
                                <>
                                    {jd.how_to_get_involved ? (
                                        <p className="text-[0.9375rem] leading-relaxed mb-6" style={{ color: BODY }}>{jd.how_to_get_involved}</p>
                                    ) : (
                                        <p className="italic text-sm mb-6" style={{ color: MUTED }}>No instructions yet.</p>
                                    )}
                                    <div className="bg-[#f0f5f5] p-6 rounded-lg mb-8 flex justify-center">
                                        <InterestFormModal clickedCommunity={displayGroupName} pipelineKey={pipelineKey}>
                                            <button
                                                type="button"
                                                className="py-3.5 px-10 rounded-xl shadow-lg uppercase tracking-widest text-sm font-bold transition-transform transform hover:-translate-y-0.5 hover:shadow-xl"
                                                style={{ backgroundColor: BRAND_RED, color: "#ffffff" }}
                                            >
                                                Express Interest in Joining
                                            </button>
                                        </InterestFormModal>
                                    </div>
                                </>
                            )}

                            {jd.updated_by?.name && jd.updated_at && (
                                <p className="text-xs mt-6 pt-4 border-t border-gray-200" style={{ color: MUTED }}>
                                    Last edited by {jd.updated_by.name} on {new Date(jd.updated_at).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    </article>

                    {/* ──────────── Gigs column (right) ──────────── */}
                    <aside className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden self-start">
                        <div className="px-7 py-8 border-b border-gray-200" style={{ backgroundColor: "rgba(0,122,138,0.06)" }}>
                            <h3 className="text-xl font-bold mb-2" style={{ color: DARK }}>The Gig Experience</h3>
                            <p className="text-sm leading-relaxed" style={{ color: BODY }}>
                                A short-term, tactical assignment with a clear deliverable and timeline.
                            </p>
                        </div>
                        {isLoadingGigs ? (
                            <div className="px-7 py-12 flex flex-col items-center text-center gap-3">
                                <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Loading Gigs...</span>
                            </div>
                        ) : gigs.length === 0 ? (
                            <div className="px-7 py-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: "rgba(0,122,138,0.1)", color: TEAL }}>
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h4 className="text-base font-bold mb-2" style={{ color: DARK }}>Available Gigs</h4>
                                <p className="text-sm leading-relaxed" style={{ color: MUTED }}>
                                    There are currently no open gigs for {displayGroupName}. Short-term opportunities will appear here when available.
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
                                {gigs.map((gig) => (
                                    <div
                                        key={gig.id}
                                        onClick={() => setSelectedGigId(gig.id)}
                                        className="group p-6 hover:bg-[#007a8a]/5 transition-colors cursor-pointer text-left"
                                    >
                                        <div className="flex justify-between items-start gap-2 mb-2.5">
                                            <span className="inline-flex items-center gap-1 bg-[#007a8a]/5 text-[#007a8a] px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">
                                                {gig.count_needed === undefined || gig.count_needed === null || Number(gig.count_needed) === 0
                                                    ? `${gig.filled_count ?? 0} Filled`
                                                    : `${gig.filled_count ?? 0}/${gig.count_needed} Slots`}
                                            </span>
                                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                                                {gig.cadence.replace(/_/g, " ")}
                                            </span>
                                        </div>
                                        <h4 className="text-sm font-black text-[#003d45] group-hover:text-[#007a8a] transition-colors leading-snug">
                                            {gig.gig_type_name}
                                        </h4>
                                        <p className="text-xs text-[#666666] line-clamp-2 mt-2 leading-relaxed">
                                            {gig.description}
                                        </p>
                                        <div className="flex gap-4 text-[10px] text-gray-400 font-semibold mt-4 pt-2 border-t border-gray-50">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3.5 h-3.5 text-[#007a8a]" />
                                                {gig.time_commitment}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Award className="w-3.5 h-3.5 text-[#007a8a]" />
                                                {gig.recognition_tier?.toLowerCase() === "tier2"
                                                    ? "Public Profile Eligible"
                                                    : gig.recognition_tier?.toLowerCase() === "tier1"
                                                    ? "Private Service Record"
                                                    : gig.recognition_tier?.replace(/tier/g, "Tier ") ?? "Standard"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </aside>
                </div>
            </div>

            {toast && (
                <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-semibold" style={{ backgroundColor: DARK, color: "#ffffff" }}>
                    {toast}
                </div>
            )}

            {editing && (
                <EditModal
                    field={editing}
                    jd={jd}
                    groupName={displayGroupName}
                    saving={saving}
                    onClose={() => setEditing(null)}
                    onSave={save}
                />
            )}

            {selectedGigId && (
                <GigModal
                    postingId={selectedGigId}
                    onClose={() => setSelectedGigId(null)}
                    onActionSuccess={() => {
                        // Re-fetch gigs on success (e.g. if volunteer applied, etc.)
                        async function refetch() {
                            try {
                                const res = await fetchWithAuth(`/api/v1/gigs/catalog?group=${entityType}:${entityId}`);
                                if (res.ok) {
                                    const data = await res.json();
                                    setGigs(data || []);
                                }
                            } catch (err) {
                                console.error("Failed to re-fetch gigs:", err);
                            }
                        }
                        refetch();
                    }}
                />
            )}
        </section>
    );
}

/* ──────────────────────────────────────────────────────────
 * Edit modal — one component handles all field types.
 * ────────────────────────────────────────────────────────── */
function EditModal({
    field,
    jd,
    groupName,
    saving,
    onClose,
    onSave,
}: {
    field: string;
    jd: JobDescription;
    groupName: string;
    saving: boolean;
    onClose: () => void;
    onSave: (partial: Partial<JobDescription>) => void;
}) {
    const [stringVal, setStringVal] = useState<string>("");

    useEffect(() => {
        setStringVal(((jd as Record<string, unknown>)[field] as string) || "");
    }, [field, jd]);

    function handleSave() {
        onSave({ [field]: stringVal.trim() } as Partial<JobDescription>);
    }

    function handleResetToDefault() {
        if (!confirm("Reset this section to the default template for this group type?")) return;
        onSave({ [field]: null } as unknown as Partial<JobDescription>);
    }

    const titleMap: Record<string, string> = {
        shape_the_future:        "Hero Tagline",
        join_the_cmt_association: "Intro Paragraph",
        mission:                 "Mission",
        how_to_get_involved:     "How to Get Involved",
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-lg font-bold" style={{ color: DARK }}>Edit — {titleMap[field] || field}</h3>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none" aria-label="Close">&times;</button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {field === "shape_the_future" ? (
                        <input
                            type="text"
                            value={stringVal}
                            onChange={(e) => setStringVal(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder="Enter text…"
                            autoFocus
                        />
                    ) : (
                        <textarea
                            value={stringVal}
                            onChange={(e) => setStringVal(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-40"
                            placeholder="Enter paragraph…"
                            autoFocus
                        />
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={handleResetToDefault}
                        disabled={saving}
                        className="text-xs font-semibold underline"
                        style={{ color: MUTED }}
                    >
                        Reset to template default
                    </button>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded border border-gray-300" style={{ color: BODY }}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            data-track="group-edit-save"
                            data-track-form-submit="group-edit"
                            className="px-4 py-2 text-sm font-bold rounded text-white"
                            style={{ backgroundColor: TEAL }}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
